<template>
  <div id="passport-record-view">

    <main class="record-page">
      <v-container v-if="passport && stat && record">
        <v-btn outlined class="back-link mb-6" :to="statRoute">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ stat.label }}
        </v-btn>

        <header class="record-hero">
          <div class="record-icon"><v-icon>{{ stat.icon }}</v-icon></div>
          <div>
            <span class="record-tag">{{ record.eyebrow }}</span>
            <h1>{{ record.title }}</h1>
            <p>{{ resolvedDescription }}</p>
          </div>
        </header>

        <section class="record-details">
          <div>
            <p class="kicker">The Record</p>
            <h2>What we have documented</h2>
          </div>
          <article>
            <ul v-if="record.details && record.details.length">
              <li v-for="detail in record.details" :key="detail">{{ detail }}</li>
            </ul>
            <p v-if="record.needsLocation" class="record-status">Venue not yet documented</p>
            <div class="record-actions">
              <a v-if="websiteUrl" :href="websiteUrl" target="_blank" rel="noopener noreferrer">
                Visit Website <v-icon small>mdi-open-in-new</v-icon>
              </a>
              <router-link
                v-for="source in record.sourceRoutes || []"
                :key="`${record.id}-${source.label}`"
                :to="source.route"
              >
                View {{ source.label }} <v-icon small>mdi-arrow-right</v-icon>
              </router-link>
            </div>
          </article>
        </section>
      </v-container>

      <EventGallery
        v-if="media.length"
        :media="media"
        :title="`${record.title} Gallery`"
      />

      <v-container v-else-if="passport && stat && record && !loading">
        <section class="empty-gallery">
          <p class="kicker">Gallery</p>
          <h2>Photos have not been tagged yet</h2>
          <p>This record is ready for images as they are connected to the dinner or experience.</p>
        </section>
      </v-container>

      <v-container v-if="!passport || !stat || !record">
        <section class="missing-state">
          <h1>Passport record not found</h1>
          <p>This record is not available for the requested passport.</p>
          <v-btn outlined class="back-link" :to="passportRoute">Back to Passport</v-btn>
        </section>
      </v-container>
    </main>

    <AppFooter></AppFooter>
  </div>
</template>

<script>
import { passportBySlug } from '../data/passports';
import { resolvePassportRecordDetails } from '../lib/passports/recordDetails';
import AppFooter from '../components/Footer.vue';
import EventGallery from '../components/reckon-way/media/EventGallery.vue';

export default {
  name: 'PassportRecordView',
  components: { AppFooter, EventGallery },
  data: () => ({
    loading: true,
    media: [],
    recordDescription: '',
    websiteUrl: '',
  }),
  computed: {
    passport() {
      return passportBySlug[this.$route.params.employeeSlug] || null;
    },
    stat() {
      return this.passport?.statDetails[this.$route.params.statKey] || null;
    },
    record() {
      return this.stat?.items.find((item) => item.id === this.$route.params.recordId) || null;
    },
    resolvedDescription() {
      return this.recordDescription || this.record?.description || '';
    },
    passportRoute() {
      return {
        name: 'passport',
        params: { employeeSlug: this.$route.params.employeeSlug },
      };
    },
    statRoute() {
      return {
        name: 'passport-stat',
        params: {
          employeeSlug: this.$route.params.employeeSlug,
          statKey: this.$route.params.statKey,
        },
      };
    },
  },
  watch: {
    '$route.fullPath': {
      handler: 'loadRecordDetails',
      immediate: true,
    },
  },
  methods: {
    async loadRecordDetails() {
      this.loading = true;
      this.media = [];
      this.recordDescription = '';
      this.websiteUrl = '';
      if (!this.record) {
        this.loading = false;
        return;
      }

      const [{ reckonWayEvents }, { galleryBySlug }] = await Promise.all([
        import('../data/reckon-way/events'),
        import('../data/adventureGalleries'),
      ]);
      const sourceGallerySlug = this.record.sourceRoutes?.[0]?.route?.params?.gallerySlug;
      const gallery = galleryBySlug[sourceGallerySlug || this.record.tripSlug] || null;
      const event = this.record.tripSlug?.startsWith('aws-reinvent-')
        ? reckonWayEvents.find((item) => item.slug === 'aws-reinvent-las-vegas')
        : reckonWayEvents.find((item) => item.slug === (sourceGallerySlug || this.record.tripSlug));
      const details = resolvePassportRecordDetails({
        record: this.record,
        statKey: this.$route.params.statKey,
        event,
        gallery,
      });
      this.media = details.media;
      this.recordDescription = details.description;
      this.websiteUrl = details.websiteUrl;
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.record-page { background: #050505; color: #f5f5f5; min-height: 75vh; padding: 48px 0 72px; }
.back-link { border-color: rgba(255,255,255,.3) !important; color: #fff !important; }
.record-hero { align-items: center; background: linear-gradient(135deg,rgba(226,50,50,.18),rgba(255,255,255,.04)); border: 1px solid rgba(226,50,50,.35); border-radius: 12px; display: grid; gap: 22px; grid-template-columns: auto minmax(0,1fr); padding: 30px; }
.record-icon { align-items: center; background: #e23232; border-radius: 50%; display: flex; height: 64px; justify-content: center; width: 64px; }
.record-icon .v-icon { color: #fff; font-size: 34px; }
.record-tag { background: rgba(232,90,90,.14); border: 1px solid rgba(232,90,90,.55); border-radius: 999px; color: #f18a8a; display: inline-block; font-size: .68rem; font-weight: 900; letter-spacing: .08em; margin-bottom: 10px; padding: 4px 9px; text-transform: uppercase; }
h1,h2,.missing-state h1 { color: #fff; letter-spacing: 0; }
h1 { font-size: clamp(2rem,5vw,3.4rem); margin: 0; }
.record-hero p { color: #d6d6d6; line-height: 1.65; margin: 10px 0 0; max-width: 780px; }
.record-details { display: grid; gap: 28px; grid-template-columns: .8fr 1.2fr; margin-top: 44px; }
.kicker { color: #e85a5a; font-size: .78rem; font-weight: 900; letter-spacing: .12em; margin: 0 0 6px; text-transform: uppercase; }
.record-details h2,.empty-gallery h2 { font-size: 1.8rem; margin: 0; }
.record-details article,.empty-gallery,.missing-state { background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.12); border-radius: 10px; padding: 24px; }
.record-details ul { list-style: none; margin: 0; padding: 0; }
.record-details li { color: #d0d0d0; }
.record-details li + li { margin-top: 7px; }
.record-status { color: #f2b84b; font-size: .82rem; font-weight: 800; margin: 16px 0 0; text-transform: uppercase; }
.record-actions { border-top: 1px solid rgba(255,255,255,.1); display: flex; flex-wrap: wrap; gap: 18px; margin-top: 18px; padding-top: 16px; }
.record-actions a { color: #ef7777; font-weight: 800; text-decoration: none; }
.record-actions .v-icon { color: currentColor; }
.empty-gallery { margin-top: 32px; }
.empty-gallery p:last-child,.missing-state p { color: #c8c8c8; margin-bottom: 0; }
.missing-state { text-align: center; }
@media(max-width:700px){.record-page{padding-top:28px}.record-hero{align-items:start;grid-template-columns:1fr}.record-details{grid-template-columns:1fr}}
</style>
